import type { Count, Ratio } from "./useFreeCreateForm";
import type { GroupPointForm } from "../blueprint";
import { normalizeRoles } from "./groupScript";

/**
 * 「出什么」产出模式 —— 2026-07-28 视觉舱精调 W4。
 *
 * ═══ 病根 ═════════════════════════════════════════════════════════════════
 * 「一次出几张」这件事以前由**三个互不相识的控件**各自表达:
 *     画面比例 [智能|1:1|4:5|…]     ← 单选
 *     多比例套图 [一次出多比例]      ← 开关,开了上面单选和下面张数都失效
 *     生成张数 [1|2|3|4]            ← 组图态下它其实被忽略,但仍可点
 * 三者互斥,却只能靠灰显 + 脚注打补丁。用户的原话是「我在爆品组图那里选了三个爆点了,
 * 底下还有张数选择」—— 他没说错,那个控件确实还在那儿,而且点了不起作用。
 *
 * ═══ 收法 ═════════════════════════════════════════════════════════════════
 * 模式是**派生量**,不新增 state(避免和 groupPoints / ratioSet 双写不同步):
 *     groupPoints != null            → GROUP      张数 = 爆点数
 *     ratioSet    != null            → RATIO_SET  张数 = 比例数
 *     否则                            → MULTI      张数 = 用户选的 count
 * 切换模式 = 一次把两个 state 摆到位(见 applyMode),不留中间态。
 */
export type OutputMode = "MULTI" | "GROUP" | "RATIO_SET";

/** 套图默认勾这三个(竖/方/横各一,覆盖抖音+朋友圈+官网)。 */
export const DEFAULT_RATIO_SET: Ratio[] = ["9:16", "1:1", "16:9"];

export function detectOutputMode(
  groupPoints: GroupPointForm[] | null,
  ratioSet: Ratio[] | null
): OutputMode {
  if (groupPoints != null) return "GROUP";
  if (ratioSet != null) return "RATIO_SET";
  return "MULTI";
}

/**
 * 切模式时两个 state 的目标值。★两个都要显式给 —— 只设一个会留下
 * 「爆点还在、套图也开着」的中间态(老代码就是这样,靠 UI 灰显掩盖)。
 */
export function applyMode(
  next: OutputMode,
  current: { groupPoints: GroupPointForm[] | null; ratioSet: Ratio[] | null }
): { groupPoints: GroupPointForm[] | null; ratioSet: Ratio[] | null } {
  switch (next) {
    case "GROUP":
      // 保留已填的爆点(用户在套图/多张之间来回切时别把内容弄丢)
      return { groupPoints: current.groupPoints ?? [], ratioSet: null };
    case "RATIO_SET":
      return { groupPoints: null, ratioSet: current.ratioSet ?? [...DEFAULT_RATIO_SET] };
    default:
      return { groupPoints: null, ratioSet: null };
  }
}

/**
 * 提交时**真正会用**的爆点集 = 写了大字的卡(空卡滤掉,role 按滤后位置归一)。
 *
 * ═══ 沿革 ═════════════════════════════════════════════════════════════════
 * 前身 `collectGroupPoints`(W4)负责收编「打了字没点添加」的草稿行 —— 那个状态
 * 本身就是交互陷阱,还得靠一行黄字解释。B1(2026-08-03)把添加改成「直接追加空卡」,
 * 草稿行退役,新的单一真相变成:**没写大字的卡不算张数、不出图**(卡面就地提示)。
 *
 * ★仍是「显示几张」与「实际出几张」的同一个函数:底栏张数、积分预估、提交侧
 * requestedCount 三处共用,口径不可能漂。
 * ★空卡滤掉后 role 必须按新位置归一(normalizeRoles)—— 否则第 1 位是空卡时,
 * 原第 2 条顶上来还带着中间位的 role,ai 侧会按显式值判出一张位置在中间的封面。
 */
export function submittableGroupPoints(points: GroupPointForm[] | null): GroupPointForm[] | null {
  if (points === null) return null;
  // ★hint(参考片那一位说了什么)是**纯前端提示态**:这里就是它的边界 ——
  //   `groupPoints` 是原样进 wire 的(见 useFreeCreateActions),多带一个键要么被后端
  //   DTO 拒,要么一路透到 ai 的 schema 上 422。剥在这一处,不在调用方各剥一遍。
  const kept = points
    .filter((p) => p.title.trim().length > 0)
    .map(({ hint: _hint, ...rest }) => rest);
  return normalizeRoles(kept);
}

/**
 * 出图前的红线闸(2026-08-14):哪几张的大字**逐字还是参考片原句**。
 *
 * 配合 `deriveGroupPoints` 改回填值:值可见解决了「看不出带了什么」,但带来 0813 那个
 * 老风险 —— 用户不改就点生成,别人家的宣传语直接印进自家物料。所以提交前挑出没动过的,
 * 让用户明确确认一次。
 *
 * ★判据是「与 hint 逐字相同」,不是「像不像」:hint 只有蓝图路由才有,用户手填的卡恒无 hint,
 *   永远不会被误拦;用户改了一个字就不再算未动过 —— 宁可漏拦也不错拦(错拦会挡住正常出图)。
 * ★只看会真出图的那几张(空卡本来就被滤掉,不该因为它弹确认)。
 */
export function untouchedReferenceTitles(points: GroupPointForm[] | null): string[] {
  if (!points) return [];
  return points
    .filter((p) => p.title.trim().length > 0 && !!p.hint && p.title.trim() === p.hint.trim())
    .map((p) => p.title.trim());
}

/**
 * 真实出图张数 —— 与后端 requestedCount 口径同源(组图=爆点数、套图=比例数、否则 count)。
 * 张数控件在非 MULTI 模式下显示这个值且**只读**。
 * ★组图传入的 groupPoints 应当是 submittableGroupPoints 的结果(滤过空卡),否则界面多算。
 */
export function effectiveCount(
  mode: OutputMode,
  opts: { groupPoints: GroupPointForm[] | null; ratioSet: Ratio[] | null; count: Count }
): number {
  if (mode === "GROUP") return opts.groupPoints?.length ?? 0;
  if (mode === "RATIO_SET") return opts.ratioSet?.length ?? 0;
  return opts.count;
}

/**
 * 这一单要不要走**智能画幅推断**(提交时调 inferRatio)。
 *
 * ★2026-07-31 e2e 抓到的口径漂移:⚙ 里组图/套图态**不显示**「智能」这一档
 * (见 `showsSmartRatio`:组图要固定比例做轮播),右回显列也写着「组图 1:1」——
 * **但提交侧照样在推断**,于是用户按 1:1 做的决定,实际发出去可能是 4:5。
 * 三处各判各的,单测测不出来(它跨三个文件),真跑一次就露了。
 *
 * 现在提交侧与底栏共用这一个判据 —— 显示什么就发什么。
 */
export function shouldInferRatio(input: {
  mode: OutputMode;
  isI2I: boolean;
  /** 文案/备注里显式写了比例(那就按用户写的,不用推) */
  textRatioFound: boolean;
  /** 用户在 ⚙ 里手选过比例 */
  ratioUserTouched: boolean;
  /** 有没有文案可推 */
  hasText: boolean;
}): boolean {
  if (input.mode !== "MULTI") return false; // 组图固定比例做轮播;套图自己在选比例
  return !input.textRatioFound && !input.ratioUserTouched && !input.isI2I && input.hasText;
}

/**
 * 「智能」比例是否该出现。
 *
 * 只在 MULTI 出现:组图要固定比例做轮播(三张画幅不一的轮播没法发),
 * 套图本身就在选比例 —— 两种语境下「智能」都是噪音,而且它还高亮在第一位,
 * 用户会以为自己没选比例。
 */
export function showsSmartRatio(mode: OutputMode, ratioFollowsSource: boolean): boolean {
  return mode === "MULTI" && !ratioFollowsSource;
}
